import { useCallback, useState } from "react";
import useAgentKnowledgeStore from "../useAgentKnowledgeStore";
import { normalizeKnowledgeIndexStatus } from "./knowledgeStatus";

export function useAgentKnowledgeActions({
  agentId,
  knowledgeFiles,
  setKnowledgeFiles,
  setKnowledgeIndexStatus,
  maxKnowledgeFiles,
}) {
  const [uploadingFile, setUploadingFile] = useState(false);
  const [indexingKnowledge, setIndexingKnowledge] = useState(false);
  const [deletingKnowledgeIndex, setDeletingKnowledgeIndex] = useState(false);

  const handleFileDrop = useCallback(
    async (acceptedFiles) => {
      if (!agentId || uploadingFile) return;
      const remaining = maxKnowledgeFiles - knowledgeFiles.length;
      if (remaining <= 0) return;
      const filesToUpload = acceptedFiles.slice(0, remaining);
      for (const file of filesToUpload) {
        setUploadingFile(true);
        try {
          const result = await useAgentKnowledgeStore
            .getState()
            .uploadKnowledgeFile(agentId, file);
          if (result?.knowledge?.files) {
            setKnowledgeFiles(result.knowledge.files);
          }
          if (result?.knowledgeIndexStatus) {
            setKnowledgeIndexStatus(
              normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
            );
          }
        } catch (e) {
          console.error("File upload failed:", e);
        } finally {
          setUploadingFile(false);
        }
      }
    },
    [
      agentId,
      uploadingFile,
      knowledgeFiles.length,
      maxKnowledgeFiles,
      setKnowledgeFiles,
      setKnowledgeIndexStatus,
    ]
  );

  const handleFileDelete = useCallback(
    async (fileId) => {
      if (!agentId) return;
      try {
        const result = await useAgentKnowledgeStore
          .getState()
          .deleteKnowledgeFile(agentId, fileId);
        if (result?.knowledge?.files) {
          setKnowledgeFiles(result.knowledge.files);
        } else {
          setKnowledgeFiles((prev) => prev.filter((f) => f.fileId !== fileId));
        }
        if (result?.knowledgeIndexStatus) {
          setKnowledgeIndexStatus(
            normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
          );
        }
      } catch (e) {
        console.error("File delete failed:", e);
      }
    },
    [agentId, setKnowledgeFiles, setKnowledgeIndexStatus]
  );

  const handleSyncKnowledge = useCallback(async () => {
    if (!agentId || indexingKnowledge) return;
    setIndexingKnowledge(true);
    let isPollingStopped = false;
    let pollingTimerId = null;
    const pollKnowledgeStatus = async () => {
      try {
        const status = await useAgentKnowledgeStore
          .getState()
          .fetchKnowledgeIndexStatus(agentId);
        if (!isPollingStopped && status) {
          setKnowledgeIndexStatus(normalizeKnowledgeIndexStatus(status));
        }
      } catch (error) {
        // Keep polling silent while indexing runs.
      }
    };
    try {
      await pollKnowledgeStatus();
      pollingTimerId = setInterval(() => {
        void pollKnowledgeStatus();
      }, 1000);
      const result = await useAgentKnowledgeStore
        .getState()
        .indexKnowledge(agentId);
      if (result?.knowledgeIndexStatus) {
        setKnowledgeIndexStatus(
          normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
        );
      } else {
        await pollKnowledgeStatus();
      }
    } catch (e) {
      console.error("Indexing failed:", e);
    } finally {
      isPollingStopped = true;
      if (pollingTimerId) {
        clearInterval(pollingTimerId);
      }
      try {
        const status = await useAgentKnowledgeStore
          .getState()
          .fetchKnowledgeIndexStatus(agentId);
        if (status) {
          setKnowledgeIndexStatus(normalizeKnowledgeIndexStatus(status));
        }
      } catch (error) {
        // Ignore trailing status refresh failures.
      }
      setIndexingKnowledge(false);
    }
  }, [agentId, indexingKnowledge, setKnowledgeIndexStatus]);

  const handleDeleteKnowledgeIndex = useCallback(async () => {
    if (!agentId || deletingKnowledgeIndex) return;
    setDeletingKnowledgeIndex(true);
    try {
      const result = await useAgentKnowledgeStore
        .getState()
        .deleteKnowledgeIndex(agentId);
      if (result?.knowledgeIndexStatus) {
        setKnowledgeIndexStatus(
          normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
        );
      } else {
        const status = await useAgentKnowledgeStore
          .getState()
          .fetchKnowledgeIndexStatus(agentId);
        setKnowledgeIndexStatus(normalizeKnowledgeIndexStatus(status));
      }
    } catch (e) {
      console.error("Delete knowledge index failed:", e);
    } finally {
      setDeletingKnowledgeIndex(false);
    }
  }, [agentId, deletingKnowledgeIndex, setKnowledgeIndexStatus]);

  return {
    uploadingFile,
    setUploadingFile,
    indexingKnowledge,
    setIndexingKnowledge,
    deletingKnowledgeIndex,
    setDeletingKnowledgeIndex,
    handleFileDrop,
    handleFileDelete,
    handleSyncKnowledge,
    handleDeleteKnowledgeIndex,
  };
}
